import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Paper, Typography, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Users } from "lucide-react";
import { API_BASE_URL, authenticatedFetch } from "../api.js";

// ---------- API helper ----------

async function joinSessionByCode(joinCode) {
  try {
    const payload = {
      joinCode: joinCode.trim().toUpperCase(),
    };

    // Use authenticatedFetch instead of regular fetch
    const res = await authenticatedFetch(`${API_BASE_URL}/sessions/join`, {
      method: "POST",
      body: JSON.stringify(payload),
    });

    const data = await res.json().catch(() => ({}));
    console.log("Join session response:", data);

    if (!res.ok) {
      return { ok: false, status: res.status, data };
    }

    return { ok: true, status: res.status, data };
  } catch (err) {
    console.error("Error joining session:", err);
    return { ok: false, status: 0, data: null };
  }
}

// ---------- Styled components (match Lobby) ----------

const Container = styled(Paper)(({ theme }) => ({
  width: "100%",
  height: "100vh",
  margin: "0 auto",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
}));

const Header = styled(Box)(({ theme }) => ({
  background: "linear-gradient(90deg, #f97316, #ef4444)",
  color: "#fff",
  padding: theme.spacing(3),
}));

const HeaderRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const HeaderTextGroup = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
}));

const HeaderSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  color: "rgba(255,255,255,0.8)",
}));

const Content = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  flex: 1,
}));

const StatusCard = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff7ed",
  borderRadius: theme.spacing(3),
  padding: theme.spacing(2.5),
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(1.5),
}));

const StatusAvatar = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: "999px",
  background: "linear-gradient(135deg, #f97316, #ef4444)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: theme.shadows[4],
}));

const StatusTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "0.95rem",
}));

const StatusText = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  color: theme.palette.text.secondary,
  lineHeight: 1.5,
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  paddingTop: theme.spacing(),
  paddingBottom: theme.spacing(),
  borderRadius: theme.spacing(2),
  fontWeight: 700,
  fontSize: "1.05rem",
  textTransform: "none",
  boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
  background: "linear-gradient(90deg, #F97316 0%, #EF4444 100%)",
  color: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1.5),
  transition: "all 150ms ease-in-out",
  "&:hover": {
    boxShadow: "0px 8px 18px rgba(0,0,0,0.25)",
    background: "linear-gradient(90deg, #FB923C 0%, #F97373 100%)",
    transform: "translateY(-1px)",
  },
  "&:active": {
    transform: "scale(0.97)",
    boxShadow: "0px 2px 8px rgba(0,0,0,0.2)",
  },
}));

const WaitingWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 160,
  textAlign: "center",
  gap: theme.spacing(1.5),
}));

const Spinner = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: "999px",
  borderWidth: 4,
  borderStyle: "solid",
  borderColor: theme.palette.warning.main,
  borderTopColor: "transparent",
  margin: "0 auto",
  marginBottom: theme.spacing(1),
  animation: "spin 1s linear infinite",
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
}));

// ---------- Component ----------

const JoinSessionByCode = () => {
  const { joinCode } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false); // Changed from true to false
  const [hasClickedJoin, setHasClickedJoin] = useState(false); // Add this
  const [errorMessage, setErrorMessage] = useState(null);
  const [errorCode, setErrorCode] = useState(null);

  // Remove the auto-join useEffect entirely and replace with this:
  const doJoin = async () => {
    if (!joinCode) {
      setErrorMessage("Invalid invite link.");
      setErrorCode("INVALID_LINK");
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    setErrorCode(null);

    const result = await joinSessionByCode(joinCode);

    if (!result.ok) {
      const backendCode = result.data?.code;
      let message = "Something went wrong joining this session.";
      let headerTitle = "Oops!";

      if (backendCode === "SESSION_FULL" || result.status === 409) {
        message =
          "This Foodie Call session is already full. Ask your partner to start a new session or create your own.";
        headerTitle = "Session is full";
      } else if (backendCode === "SESSION_NOT_FOUND" || result.status === 404) {
        message =
          "That join code doesn't match any active session. Double-check the link or ask your friend to resend it.";
        headerTitle = "Session not found";
      } else if (backendCode === "SESSION_EXPIRED" || result.status === 410) {
        message =
          "This session has already finished or expired. Ask your friend to start a new one.";
        headerTitle = "Session expired";
      } else if (result.status === 0) {
        message =
          "We couldn't reach the server. Check your connection and try again.";
        headerTitle = "Connection issue";
      }

      setLoading(false);
      setErrorMessage(message);
      setErrorCode(backendCode || headerTitle.toUpperCase());
      return;
    }

    const raw = result.data;
    const session =
      raw?.session ??
      raw?.Item ??
      (Array.isArray(raw?.sessions) ? raw.sessions[0] : null) ??
      raw;

    if (!session || (!session.session_id && !session.sessionId)) {
      console.error(
        "JoinSessionByCode: missing session or session_id/sessionId in response",
        { result, session }
      );
      setLoading(false);
      setErrorMessage(
        "Sorry, something went wrong joining that session. Please try again."
      );
      setErrorCode("MISSING_SESSION_ID");
      return;
    }

    const sessionId = session.session_id ?? session.sessionId;
    const status = session.status;
    const partySizeRaw = session.party_size;
    const partySize = Number(partySizeRaw);
    const participantCount =
      typeof session.participantCount === "number"
        ? session.participantCount
        : 0;
    const joinCodeUpper = (session.join_code || joinCode || "")
      .trim()
      .toUpperCase();

    let targetPath = "";

    if (
      status === "active" ||
      (Number.isFinite(partySize) && participantCount === partySize)
    ) {
      targetPath = `/session/${sessionId}/match`;
    } else {
      targetPath = `/session/${sessionId}/lobby`;
    }

    console.log("JoinSessionByCode navigating to:", targetPath);
    navigate(targetPath, {
      replace: true,
      state: {
        session,
        joinCode: joinCodeUpper,
        isHost: false,
      },
    });
  };

  const handleJoinClick = () => {
    setHasClickedJoin(true);
    doJoin();
  };

  // ---------- Initial state (before clicking join) ----------
  if (!hasClickedJoin && !errorMessage) {
    return (
      <Container>
        <Header>
          <HeaderRow>
            <HeaderTextGroup>
              <HeaderTitle variant="h6">Join Foodie Call Session</HeaderTitle>
              <HeaderSubtitle>
                You've been invited to find a restaurant together!
              </HeaderSubtitle>
            </HeaderTextGroup>
          </HeaderRow>
        </Header>

        <Content>
          <StatusCard>
            <StatusAvatar>
              <Users size={32} color="#ffffff" />
            </StatusAvatar>
            <StatusTitle>Ready to join?</StatusTitle>
            <StatusText>
              Session Code: <strong>{joinCode?.toUpperCase()}</strong>
            </StatusText>
            <StatusText>
              Click below to join your partner and start voting on restaurants
              together!
            </StatusText>
          </StatusCard>

          <PrimaryButton onClick={handleJoinClick}>Join Session</PrimaryButton>
        </Content>
      </Container>
    );
  }

  // ---------- Loading state (after clicking join) ----------
  if (loading) {
    return (
      <Container>
        <Header>
          <HeaderRow>
            <HeaderTextGroup>
              <HeaderTitle variant="h6">Joining your session…</HeaderTitle>
              <HeaderSubtitle>
                Hang tight while we connect you and your partner.
              </HeaderSubtitle>
            </HeaderTextGroup>
          </HeaderRow>
        </Header>

        <Content>
          <StatusCard>
            <StatusAvatar>
              <Users size={32} color="#ffffff" />
            </StatusAvatar>
            <WaitingWrapper>
              <Spinner />
              <StatusTitle>Looking up your session</StatusTitle>
              <StatusText>
                We're verifying your invite link and getting your Foodie Call
                session ready.
              </StatusText>
            </WaitingWrapper>
          </StatusCard>
        </Content>
      </Container>
    );
  }

  // ---------- Error state ----------
  if (errorMessage) {
    const isFull = errorCode === "SESSION_FULL";

    return (
      <Container>
        <Header>
          <HeaderRow>
            <HeaderTextGroup>
              <HeaderTitle variant="h6">
                {isFull ? "Session is full" : "We couldn't join this session"}
              </HeaderTitle>
              <HeaderSubtitle>
                Let's get you back to a fresh start.
              </HeaderSubtitle>
            </HeaderTextGroup>
          </HeaderRow>
        </Header>

        <Content>
          <StatusCard>
            <StatusAvatar>
              <Users size={32} color="#ffffff" />
            </StatusAvatar>
            <StatusTitle>
              {isFull ? "Try a new session" : "Something went wrong"}
            </StatusTitle>
            <StatusText>{errorMessage}</StatusText>
          </StatusCard>

          <PrimaryButton
            variant="contained"
            color="primary"
            onClick={() => navigate("/", { replace: true })}
          >
            Return to Home
          </PrimaryButton>
        </Content>
      </Container>
    );
  }

  return null;
};

export default JoinSessionByCode;
